import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'crypn_ui_preferences_v1';

type Density = 'comfortable' | 'compact';
type Contrast = 'standard' | 'high';
type Theme = 'light' | 'dark';

interface UiPreferences {
  density: Density;
  contrast: Contrast;
  theme: Theme;
}

interface UiPreferencesContextValue extends UiPreferences {
  toggleDensity: () => void;
  toggleContrast: () => void;
  toggleTheme: () => void;
  setTheme: (next: Theme) => void;
}

const defaults: UiPreferences = {
  density: 'comfortable',
  contrast: 'standard',
  theme: 'dark',
};

const UiPreferencesContext = createContext<UiPreferencesContextValue>({
  ...defaults,
  toggleDensity: () => {},
  toggleContrast: () => {},
  toggleTheme: () => {},
  setTheme: () => {},
});

const readStoredPreferences = (): UiPreferences => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw) as Partial<UiPreferences>;
    return {
      density: parsed?.density === 'compact' ? 'compact' : defaults.density,
      contrast: parsed?.contrast === 'high' ? 'high' : defaults.contrast,
      theme: parsed?.theme === 'light' ? 'light' : defaults.theme,
    };
  } catch {
    return defaults;
  }
};

interface UiPreferencesProviderProps {
  children: React.ReactNode;
}

export const UiPreferencesProvider = ({ children }: UiPreferencesProviderProps) => {
  const [prefs, setPrefs] = useState<UiPreferences>(() => {
    if (typeof window === 'undefined') {
      return defaults;
    }
    return readStoredPreferences();
  });

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.body.dataset.density = prefs.density;
    document.body.dataset.contrast = prefs.contrast;
    document.documentElement.setAttribute('data-theme', prefs.theme);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const value = useMemo<UiPreferencesContextValue>(
    () => ({
      ...prefs,
      toggleDensity: () => {
        setPrefs((prev) => ({
          ...prev,
          density: prev.density === 'comfortable' ? 'compact' : 'comfortable',
        }));
      },
      toggleContrast: () => {
        setPrefs((prev) => ({
          ...prev,
          contrast: prev.contrast === 'standard' ? 'high' : 'standard',
        }));
      },
      toggleTheme: () => {
        setPrefs((prev) => ({
          ...prev,
          theme: prev.theme === 'light' ? 'dark' : 'light',
        }));
      },
      setTheme: (next: Theme) => {
        setPrefs((prev) => ({ ...prev, theme: next }));
      },
    }),
    [prefs],
  );

  return <UiPreferencesContext.Provider value={value}>{children}</UiPreferencesContext.Provider>;
};

export const useUiPreferences = (): UiPreferencesContextValue => useContext(UiPreferencesContext);
